/**
 * Orchestrators that run the "save key → publish → back-fill UDS record"
 * dance for one template entity at a time. Phases 2-6 import from here so
 * the moving parts (module discovery, batching, publish, back-fill) live in
 * exactly one place.
 *
 * Each function:
 *   1. Resolves the template's UILM moduleId (creates the module if new).
 *   2. Builds resources (we currently push only en-US — admin can run
 *      Auto-Translate from the Translations Tab to fill de-DE).
 *   3. Saves keys via the service.
 *   4. Calls `generateUilmFile` so the new keys appear on the CDN.
 *   5. Writes the key strings back into the UDS record's `uilm*Key`
 *      fields so downstream consumers can look up the key without
 *      re-running the formula.
 *
 * On failure, throws — callers decide whether to surface to the user or
 * swallow (e.g. on template create we treat UILM failure as non-fatal
 * because the template itself was already persisted).
 */

import { uilmKeyForField } from '@/i18n/utils/uilm-key-for-field';
import { uilmKeyForOption } from '@/i18n/utils/uilm-key-for-option';
import { uilmKeyForScreen } from '@/i18n/utils/uilm-key-for-screen';
import { uilmKeyForTemplate } from '@/i18n/utils/uilm-key-for-template';
import {
  getTemplateByIdWithScreensAndFields,
  updateTemplate,
  updateTemplateField,
  updateTemplateScreen,
} from '@/modules/templates/services/templates.service';
import type { TemplateWithScreens } from '@/modules/templates/types/templates.types';
import { buildTemplateKeys, type KeyPlanEntry } from './build-template-keys';
import { slugToCamelCase } from '@/i18n/utils/slug-to-camel-case';
import { DEFAULT_LANGUAGE_CULTURE } from './uilm-constants';
import {
  ensureModuleExistenceLoaded,
  isKnownKey,
  markKeyAsKnown,
} from './uilm-existence-cache';
import { getOrCreateTemplateModule } from './uilm-module-discovery';
import { clearUnpublished, markUnpublished } from './unpublished-tracker';
import {
  generateUilmFile,
  getUilmKeysByModule,
  getUilmKeysByNames,
  saveUilmKeys,
  type UilmKey,
  type UilmResource,
} from '../services/uilm-admin.service';

// Build a single-locale resource array. Used by `seedTemplateUilm` for keys
// that don't yet exist on UILM (no other locales to preserve).
const enResource = (value: string): UilmResource[] => [
  {
    value,
    culture: DEFAULT_LANGUAGE_CULTURE,
    characterLength: value.length,
  },
];

/**
 * Merge a fresh en-US value into an existing key's resource list,
 * preserving every non-EN resource the admin has already translated.
 *
 * Critical for the per-entity sync flows: when an admin edits a screen
 * title in the builder we only know the new EN text — calling
 * `saveUilmKey` with `[en]` would otherwise wipe the existing `de-DE`
 * resource since UILM treats the array as authoritative.
 */
function mergeEnResource(
  existing: UilmKey | undefined,
  newEnValue: string,
): UilmResource[] {
  const fresh: UilmResource = {
    value: newEnValue,
    culture: DEFAULT_LANGUAGE_CULTURE,
    characterLength: newEnValue.length,
  };
  if (!existing) return [fresh];
  const preserved = existing.resources.filter(
    (r) => r.culture !== DEFAULT_LANGUAGE_CULTURE,
  );
  return [fresh, ...preserved];
}

/**
 * Save UILM keys for a set of intents, skipping the existence/no-op
 * fetch wherever the existence cache can tell us a key is brand new.
 *
 * Two paths based on the session-scoped existence cache
 * ([uilm-existence-cache.ts]):
 *
 *   1. **New keys** (cache says the key is not on UILM): no fetch
 *      needed. There's no remote EN to compare against and no non-EN
 *      resource to preserve, so we save with `isNewKey: true` and a
 *      single-EN `resources[]`. Empty values are dropped — we don't
 *      create blank keys.
 *
 *   2. **Existing keys** (cache says the key exists): fetch via
 *      `getUilmKeysByNames` so we can both skip no-op pushes (remote
 *      EN already matches) and preserve any non-EN resources (e.g.
 *      `de-DE`). Without the merge step, an EN-only save would wipe
 *      DE — that's the bug `mergeEnResource` exists to prevent.
 *
 * After a successful save, newly-created keys are marked known in the
 * cache so subsequent edits route through path 2.
 *
 * `saveUilmKeys` auto-publishes at the end of its batch, so a typical
 * build-out (lots of new fields/screens) makes exactly one network
 * round trip per save action: `/Key/Save` only.
 */
async function pushKeysPreservingNonEn(
  moduleId: string,
  slug: string,
  intents: { keyName: string; enValue: string }[],
): Promise<void> {
  if (intents.length === 0) return;

  await ensureModuleExistenceLoaded(moduleId, slugToCamelCase(slug));

  // Split by whether the key is already on UILM. Only existing keys
  // trigger the fetch — new keys go straight to save.
  const newIntents: typeof intents = [];
  const existingIntents: typeof intents = [];
  for (const intent of intents) {
    if (isKnownKey(moduleId, intent.keyName)) {
      existingIntents.push(intent);
    } else {
      newIntents.push(intent);
    }
  }

  // Only fetch the existing slice.
  const existing =
    existingIntents.length > 0
      ? await getUilmKeysByNames(
          moduleId,
          existingIntents.map((i) => i.keyName),
        )
      : [];
  const byName = new Map(existing.map((k) => [k.keyName, k]));

  const keys: { keyName: string; resources: UilmResource[]; isNewKey: boolean }[] = [];

  for (const { keyName, enValue } of newIntents) {
    // Don't create a brand-new key for an empty value — clutter.
    if (enValue === '') continue;
    keys.push({
      keyName,
      resources: [
        { value: enValue, culture: DEFAULT_LANGUAGE_CULTURE, characterLength: enValue.length },
      ],
      isNewKey: true,
    });
  }

  for (const { keyName, enValue } of existingIntents) {
    const remote = byName.get(keyName);
    const remoteEn = remote?.resources.find(
      (r) => r.culture === DEFAULT_LANGUAGE_CULTURE,
    )?.value;
    // Remote exists AND its EN matches → nothing to push for this key.
    if (remote && remoteEn === enValue) continue;
    keys.push({
      keyName,
      // `isNewKey` is `false` here even when `remote` is undefined — the
      // cache said the key exists. If UILM rejects we'll learn from the
      // error (rare; would mean the cache went stale). For the typical
      // case the cache is authoritative.
      resources: mergeEnResource(remote, enValue),
      isNewKey: false,
    });
  }

  if (keys.length === 0) return;

  await saveUilmKeys(moduleId, keys);

  // Promote the keys we just created to "known" so the next edit on the
  // same key uses the existing-key path (and its DE-preserving merge).
  for (const { keyName, enValue } of newIntents) {
    if (enValue !== '') markKeyAsKnown(moduleId, keyName);
  }
}

export interface SyncTemplateSaveInput {
  templateId: string;
  slug: string;
  title: string;
  description?: string | null;
}

/**
 * Sync a template's title + description to UILM. Idempotent — safe to call
 * on both create and update flows:
 *   - Module discovery returns the existing moduleId after the first call.
 *   - `pushKeysPreservingNonEn` overwrites EN but preserves any DE / other
 *     locale resources already on UILM.
 *   - The back-fill `updateTemplate({UilmTitleKey, UilmDescriptionKey})`
 *     writes the same key strings the formula always produces, so a
 *     second call is a no-op at the UDS level.
 *
 * Called from:
 *   - `admin-templates-new-page.tsx` after `createTemplate` succeeds
 *   - `details-tab.tsx` after `updateTemplate` succeeds
 */
export async function syncTemplateSave({
  templateId,
  slug,
  title,
  description,
}: SyncTemplateSaveInput): Promise<void> {
  const moduleId = await getOrCreateTemplateModule(slug);

  const titleKey = uilmKeyForTemplate(slug, 'title');
  const descKey = uilmKeyForTemplate(slug, 'desc');

  // Always include both intents — `pushKeysPreservingNonEn` skips no-ops
  // and refuses to create brand-new keys with empty values, so a cleared
  // description correctly overwrites the remote one while a screen that
  // never had a description doesn't generate a junk empty key.
  await pushKeysPreservingNonEn(moduleId, slug, [
    { keyName: titleKey, enValue: title },
    { keyName: descKey, enValue: description ?? '' },
  ]);

  // Back-fill the UDS record so the template carries its own key names.
  await updateTemplate(templateId, {
    UilmTitleKey: titleKey,
    UilmDescriptionKey: description ? descKey : '',
  });

  markUnpublished(slug);
}

export interface SyncScreenSaveInput {
  templateSlug: string;
  screenId: string;
  title: string;
  description?: string | null;
}

/**
 * Sync one screen's title + description to UILM. Runs after the screen
 * has been persisted in UDS (so `screenId` is the canonical UDS ItemId).
 *
 * Module discovery is idempotent — the second screen save on a template
 * just looks the module up; only the very first save creates it.
 */
export async function syncScreenSave({
  templateSlug,
  screenId,
  title,
  description,
}: SyncScreenSaveInput): Promise<void> {
  const moduleId = await getOrCreateTemplateModule(templateSlug);

  const titleKey = uilmKeyForScreen(templateSlug, screenId, 'title');
  const descKey = uilmKeyForScreen(templateSlug, screenId, 'desc');

  // Both intents always included — the helper handles "skip new empty
  // key" and "skip no-op push" cases internally.
  await pushKeysPreservingNonEn(moduleId, templateSlug, [
    { keyName: titleKey, enValue: title },
    { keyName: descKey, enValue: description ?? '' },
  ]);

  await updateTemplateScreen(screenId, {
    UilmTitleKey: titleKey,
    UilmDescriptionKey: description ? descKey : '',
  });

  markUnpublished(templateSlug);
}

export interface SyncFieldSaveInput {
  templateSlug: string;
  screenId: string;
  fieldId: string;
  label: string;
  helpText?: string | null;
  placeholder?: string | null;
  /** Field type — drives whether option keys are generated. */
  type: string;
  /**
   * Current option values in their canonical order. The resulting
   * `uilmOptionsKeys[]` array is written back to UDS in the SAME order,
   * which is what keeps the indices aligned with `options[]` after
   * reorders / adds / removes.
   */
  options?: string[];
}

/**
 * Sync a created or updated field's label / help / placeholder / option
 * keys to UILM, then back-fill the matching `Uilm*Key` UDS fields. Only
 * select / multiselect fields generate option keys; other types push an
 * empty array so any leftover option keys from a previous type are cleared.
 */
export async function syncFieldSave({
  templateSlug,
  screenId,
  fieldId,
  label,
  helpText,
  placeholder,
  type,
  options,
}: SyncFieldSaveInput): Promise<void> {
  const moduleId = await getOrCreateTemplateModule(templateSlug);

  const labelKey = uilmKeyForField(templateSlug, screenId, fieldId, 'label');
  const helpKey = uilmKeyForField(templateSlug, screenId, fieldId, 'help');
  const placeholderKey = uilmKeyForField(templateSlug, screenId, fieldId, 'placeholder');

  // Always include label / help / placeholder. The helper drops new-empty
  // keys (so a field that never had helpText doesn't generate one) AND
  // pushes empty-clearing edits (so removing helpText updates UILM).
  const intents: { keyName: string; enValue: string }[] = [
    { keyName: labelKey, enValue: label },
    { keyName: helpKey, enValue: helpText ?? '' },
    { keyName: placeholderKey, enValue: placeholder ?? '' },
  ];

  // Options — only meaningful for select / multiselect. For other types we
  // emit no option keys and the back-fill below writes an empty array.
  const isOptionType = type === 'select' || type === 'multiselect';
  const optionKeys: string[] = [];
  if (isOptionType && Array.isArray(options)) {
    options.forEach((opt) => {
      if (!opt) return;
      const optKey = uilmKeyForOption(templateSlug, screenId, fieldId, opt);
      optionKeys.push(optKey);
      intents.push({ keyName: optKey, enValue: opt });
    });
  }

  await pushKeysPreservingNonEn(moduleId, templateSlug, intents);

  await updateTemplateField(fieldId, {
    UilmLabelKey: labelKey,
    UilmHelpTextKey: helpText ? helpKey : '',
    UilmPlaceholderKey: placeholder ? placeholderKey : '',
    UilmOptionsKeys: optionKeys,
  });

  markUnpublished(templateSlug);
}

// ---------------------------------------------------------------------------
// Bulk seed (Phase 5 — Translations Tab; Phase 6 — Import / Overwrite)
// ---------------------------------------------------------------------------

export interface SeedTemplateResult {
  moduleId: string;
  /** Total keys the template should have (whether on UILM or not). */
  totalKeys: number;
  /** Keys we just created on UILM in this run. */
  pushedKeys: number;
  /** Keys that already existed on UILM and were left alone. */
  alreadyOnUilm: number;
}

/**
 * Seed a system template's translation footprint to UILM end-to-end.
 *
 * Used by:
 *   - the Translations Tab "Seed UILM Keys" button (Phase 5),
 *   - the Import flow after a fresh template is created (Phase 6),
 *   - the Overwrite flow after a template's content is replaced (Phase 6).
 *
 * Pushes only keys that don't already exist on UILM — drift on existing
 * keys (different EN values) is surfaced as a "mismatch" in the diff but
 * NOT auto-resolved here. The admin reconciles manually.
 */
export async function seedTemplateUilm(templateId: string): Promise<SeedTemplateResult> {
  const template = await getTemplateByIdWithScreensAndFields(templateId);
  if (!template) throw new Error(`Template ${templateId} not found`);
  if (template.organizationId) {
    throw new Error('Only system templates can be seeded to UILM');
  }

  const moduleId = await getOrCreateTemplateModule(template.slug);

  const localPlan = buildTemplateKeys({
    template,
    screens: template.screens,
  });

  const remoteKeys = await getUilmKeysByModule(moduleId);
  const remoteKeyNames = new Set(remoteKeys.map((k) => k.keyName));
  const missing = localPlan.filter((p) => !remoteKeyNames.has(p.keyName));

  if (missing.length > 0) {
    const keysToSave = missing.map((p) => ({
      keyName: p.keyName,
      resources: enResource(p.enValue),
    }));
    // Defer publish — we still want the UDS back-fill below to run before
    // the CDN sees the new keys, so opt out of the batch's auto-publish
    // and generate explicitly after back-fill completes.
    await saveUilmKeys(moduleId, keysToSave, false);
    await generateUilmFile(moduleId);
    // Promote freshly-pushed keys in the existence cache so the next
    // per-entity sync takes the existing-key (DE-preserving) path.
    for (const p of missing) markKeyAsKnown(moduleId, p.keyName);
  }
  // Also promote keys that already existed on UILM but weren't yet in
  // the cache — the seed proved they exist, so cache them up.
  for (const name of remoteKeyNames) markKeyAsKnown(moduleId, name);

  // Always back-fill UDS records — even if a key already existed on UILM,
  // the local record's `uilm*Key` may be stale or empty (legacy templates).
  await backFillUdsFromPlan(localPlan);

  // Seed only publishes when it actually pushed new keys (inline above).
  // In that case, any other pending unpublished edits for this template
  // also became live, so we can clear the dirty flag.
  if (missing.length > 0) {
    clearUnpublished(template.slug);
  }

  return {
    moduleId,
    totalKeys: localPlan.length,
    pushedKeys: missing.length,
    alreadyOnUilm: localPlan.length - missing.length,
  };
}

/**
 * Write the key strings from a `KeyPlanEntry[]` back into the UDS records
 * they describe. Entries are grouped by owner so we issue one `update*`
 * call per template/screen/field instead of one per key. Options collapse
 * into their parent field's `UilmOptionsKeys[]` array.
 */
async function backFillUdsFromPlan(plan: KeyPlanEntry[]): Promise<void> {
  type Group = { type: KeyPlanEntry['ownerType']; entries: KeyPlanEntry[] };
  const byOwner = new Map<string, Group>();
  for (const entry of plan) {
    // Options are owned by the parent field UDS record — group with field entries.
    const groupOwnerType = entry.ownerType === 'option' ? 'field' : entry.ownerType;
    const key = `${groupOwnerType}:${entry.ownerId}`;
    const existing = byOwner.get(key);
    if (existing) {
      existing.entries.push(entry);
    } else {
      byOwner.set(key, { type: groupOwnerType, entries: [entry] });
    }
  }

  for (const [key, group] of byOwner.entries()) {
    const ownerId = key.split(':')[1];

    if (group.type === 'template') {
      const update: Record<string, string> = {};
      for (const e of group.entries) update[toPascalCase(e.uilmField)] = e.keyName;
      await updateTemplate(ownerId, update);
    } else if (group.type === 'screen') {
      const update: Record<string, string> = {};
      for (const e of group.entries) update[toPascalCase(e.uilmField)] = e.keyName;
      await updateTemplateScreen(ownerId, update);
    } else if (group.type === 'field') {
      const update: Record<string, unknown> = {};
      const optionKeysByIndex: string[] = [];
      let maxIndex = -1;
      for (const e of group.entries) {
        if (e.uilmField === 'uilmOptionsKeys' && typeof e.optionIndex === 'number') {
          optionKeysByIndex[e.optionIndex] = e.keyName;
          if (e.optionIndex > maxIndex) maxIndex = e.optionIndex;
        } else {
          update[toPascalCase(e.uilmField)] = e.keyName;
        }
      }
      if (maxIndex >= 0) {
        // Densify so the array is never sparse — write '' for any gap.
        update.UilmOptionsKeys = Array.from(
          { length: maxIndex + 1 },
          (_, i) => optionKeysByIndex[i] ?? '',
        );
      }
      await updateTemplateField(ownerId, update);
    }
  }
}

const toPascalCase = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

// ---------------------------------------------------------------------------
// Mismatch reconciliation (Translations Tab "Keep local" / "Keep UILM")
// ---------------------------------------------------------------------------

/**
 * "Keep local" — overwrite UILM's EN resource for one key with the local
 * value. Non-EN resources (e.g. de-DE) are preserved via the standard
 * `pushKeysPreservingNonEn` path, which now auto-publishes via the
 * batched `saveUilmKeys` default. The admin sees the result live on
 * UILM the moment this returns.
 */
export async function pushLocalEnToUilm(
  moduleId: string,
  slug: string,
  keyName: string,
  localEnValue: string,
): Promise<void> {
  await pushKeysPreservingNonEn(moduleId, slug, [{ keyName, enValue: localEnValue }]);
}

/** Maps a `uilmField` on a TemplateField record to the content field it shadows. */
const FIELD_KEY_TO_CONTENT: Record<string, string> = {
  uilmLabelKey: 'Label',
  uilmHelpTextKey: 'HelpText',
  uilmPlaceholderKey: 'Placeholder',
};

function findFieldInTemplate(template: TemplateWithScreens, fieldId: string) {
  for (const screen of template.screens) {
    const field = screen.fields.find((f) => f.id === fieldId);
    if (field) return field;
  }
  return null;
}

/**
 * "Keep UILM" — update the local UDS record so its EN text matches UILM.
 * The UDS field varies by entity type:
 *   - template / screen  →  `Title` or `Description`
 *   - field              →  `Label`, `HelpText`, or `Placeholder`
 *   - option             →  parent field's `Options[index]`
 *
 * After this returns, the next diff run will mark the key as in-sync.
 * Note: renaming an option leaves the original `..._OPT_<old>` key on
 * UILM as an orphan and generates a new `..._OPT_<new>` on the next
 * sync — by design, since the formula is value-derived.
 */
export async function pullUilmEnToLocal(
  plan: KeyPlanEntry,
  remoteEnValue: string,
  template: TemplateWithScreens,
): Promise<void> {
  if (plan.ownerType === 'template') {
    const field = plan.uilmField === 'uilmTitleKey' ? 'Title' : 'Description';
    await updateTemplate(plan.ownerId, { [field]: remoteEnValue });
    return;
  }

  if (plan.ownerType === 'screen') {
    const field = plan.uilmField === 'uilmTitleKey' ? 'Title' : 'Description';
    await updateTemplateScreen(plan.ownerId, { [field]: remoteEnValue });
    return;
  }

  if (plan.ownerType === 'field') {
    const contentField = FIELD_KEY_TO_CONTENT[plan.uilmField];
    if (!contentField) return;
    await updateTemplateField(plan.ownerId, { [contentField]: remoteEnValue });
    return;
  }

  // option — rewrite the parent field's options array at the entry's index.
  if (plan.ownerType === 'option' && plan.optionIndex != null) {
    const field = findFieldInTemplate(template, plan.ownerId);
    if (!field) return;
    const newOptions = [...(field.options ?? [])];
    newOptions[plan.optionIndex] = remoteEnValue;
    await updateTemplateField(plan.ownerId, { Options: newOptions });
  }
}
